import { useState, useEffect } from "react";
import { apiGet } from "../services/api";

/**
 * Hook to fetch and manage rate tiers for a staffing organization.
 * @param {string} organizationId - The organization ID to fetch tiers for
 * @returns {object} - { tiers, loading, error, refetch, calculateRate }
 */
export function useRateTiers(organizationId) {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTiers = async () => {
    if (!organizationId) {
      setTiers([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await apiGet(
        `/rate-tiers?organizationId=${organizationId}`,
      );
      setTiers(response?.tiers || response || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching rate tiers:", err);
      setError(err.message || "Failed to load rate tiers");
      setTiers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTiers();
  }, [organizationId]);

  const calculateRate = async (tierId, location = "default") => {
    try {
      const response = await api.post(`/rate-tiers/${tierId}/calculate-rate`, {
        location,
      });
      return {
        baseRate: response.data.baseRate,
        calculatedRate: response.data.calculatedRate,
        adjustment: response.data.adjustment,
      };
    } catch (err) {
      console.error("Error calculating rate:", err);
      throw err;
    }
  };

  return {
    tiers,
    loading,
    error,
    refetch: fetchTiers,
    calculateRate,
  };
}

export const useWageTiers = useRateTiers;
